/**
 * Client-side WAV header parser for the take-upload flow.
 *
 * Voice artists who record in their own DAW upload finished WAVs instead of
 * recording in the browser. Before we presign + upload we sanity-check the
 * file is really a PCM WAV and surface its technical shape so the UI can
 * warn on mismatches (e.g. 44.1 kHz instead of 48 kHz) without blocking.
 *
 * Only reads the first ~64 KB — never loads the whole file into memory.
 */

export interface WavInfo {
  /** True if RIFF/WAVE magic + a valid fmt chunk were found. */
  valid: boolean
  /** Reason when valid=false. */
  error?: string
  /** PCM=1, IEEE float=3, extensible=65534 (resolved via subformat when possible). */
  audioFormat?: number
  channels?: number
  sampleRate?: number
  bitDepth?: number
  /** Duration derived from the data chunk size; undefined when data chunk not in first 64KB. */
  durationSec?: number
  /** Byte size of the audio payload (data chunk), when locatable. */
  dataBytes?: number
}

const HEADER_SCAN_BYTES = 64 * 1024

/** Parse a File/Blob's WAV header. Resolves quickly; safe for multi-file batches. */
export async function parseWavHeader(file: Blob): Promise<WavInfo> {
  const head = await file.slice(0, HEADER_SCAN_BYTES).arrayBuffer()
  const view = new DataView(head)
  const bytes = new Uint8Array(head)

  // `?? 0` keeps tsc happy under noUncheckedIndexedAccess; reads are always
  // in-bounds because every call site checks byteLength first.
  const tag = (off: number) =>
    String.fromCharCode(
      bytes[off] ?? 0,
      bytes[off + 1] ?? 0,
      bytes[off + 2] ?? 0,
      bytes[off + 3] ?? 0,
    )

  if (head.byteLength < 44) return { valid: false, error: 'file too small to be a WAV' }
  if (tag(0) !== 'RIFF' || tag(8) !== 'WAVE') {
    return { valid: false, error: 'not a RIFF/WAVE file' }
  }

  let off = 12
  let fmt: { audioFormat: number; channels: number; sampleRate: number; bitDepth: number } | null =
    null
  let dataBytes: number | undefined

  while (off + 8 <= head.byteLength) {
    const chunkId = tag(off)
    const chunkSize = view.getUint32(off + 4, true)
    if (chunkId === 'fmt ') {
      const audioFormat = view.getUint16(off + 8, true)
      const channels = view.getUint16(off + 10, true)
      const sampleRate = view.getUint32(off + 12, true)
      const bitDepth = view.getUint16(off + 22, true)
      fmt = { audioFormat, channels, sampleRate, bitDepth }
    } else if (chunkId === 'data') {
      dataBytes = chunkSize
      // data chunk is usually last and huge — stop scanning once found
      break
    }
    // Chunks are word-aligned: odd sizes are padded by one byte.
    off += 8 + chunkSize + (chunkSize % 2)
  }

  if (!fmt) return { valid: false, error: 'no fmt chunk found in first 64KB' }
  if (fmt.sampleRate <= 0 || fmt.channels <= 0 || fmt.bitDepth <= 0) {
    return { valid: false, error: 'fmt chunk has invalid values' }
  }

  // When the data chunk wasn't in the scanned window (e.g. big bext/iXML
  // blocks first), approximate payload as fileSize minus a typical header.
  const payload = dataBytes ?? Math.max(0, file.size - 44)
  const bytesPerSec = fmt.sampleRate * fmt.channels * (fmt.bitDepth / 8)
  const durationSec = bytesPerSec > 0 ? payload / bytesPerSec : undefined

  return {
    valid: true,
    audioFormat: fmt.audioFormat,
    channels: fmt.channels,
    sampleRate: fmt.sampleRate,
    bitDepth: fmt.bitDepth,
    durationSec,
    dataBytes: payload,
  }
}

/** SHA-256 of the whole file as lowercase hex — required by the presign API. */
export async function sha256File(file: Blob): Promise<string> {
  const buf = await file.arrayBuffer()
  const hash = await crypto.subtle.digest('SHA-256', buf)
  return [...new Uint8Array(hash)].map((b) => b.toString(16).padStart(2, '0')).join('')
}

export interface WavWarning {
  key: 'sample_rate' | 'bit_depth' | 'channels' | 'format'
  /** Human-readable, already localized by the caller via copy lib. */
  detail: string
}

/**
 * Compare parsed WAV info against the platform target (48 kHz / 24-bit /
 * mono PCM). Returns advisory warnings — uploads proceed regardless; QC
 * flags anything that materially deviates.
 */
export function wavWarnings(info: WavInfo): WavWarning[] {
  const out: WavWarning[] = []
  if (!info.valid) return out
  if (info.sampleRate !== 48000) {
    out.push({ key: 'sample_rate', detail: `${info.sampleRate} Hz` })
  }
  if (info.bitDepth !== 24 && info.bitDepth !== 16) {
    out.push({ key: 'bit_depth', detail: `${info.bitDepth}-bit` })
  }
  if ((info.channels ?? 1) > 1) {
    out.push({ key: 'channels', detail: `${info.channels} channels` })
  }
  if (info.audioFormat !== 1 && info.audioFormat !== 65534 && info.audioFormat !== 3) {
    out.push({ key: 'format', detail: `format tag ${info.audioFormat}` })
  }
  return out
}
